
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Monitor, Smartphone, ArrowRight, Sparkles, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IntegratedCalculatorContent } from '@/components/calculator/IntegratedCalculatorInterface';
import { MobileCalculatorContent } from '@/components/calculator/MobileCalculatorInterface';
import { SaveCalculationModal } from '@/components/calculator/SaveCalculationModal';
import { AutoCalculateOptions } from '@/components/calculator/utils/types';
import { useCalculatorDraft } from '@/hooks/drafts/useCalculatorDraft';
import { useUnifiedCalculator } from '@/hooks/useUnifiedCalculator';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { images } from '@/assets/images';
import { cn } from '@/lib/utils';
import { detectMobileImmediate } from '@/hooks/useMobileRedirect';
import { CalculatorNav } from '@/components/calculator/CalculatorNav';
import { PageTransition } from '@/components/layout/PageTransition';

type ViewMode = 'desktop' | 'mobile';

const TryCalculator: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  // Initialize with immediate mobile detection to prevent flash
  const [viewMode, setViewMode] = useState<ViewMode>(() => 
    detectMobileImmediate() ? 'mobile' : 'desktop'
  );
  const [isAutoDetected, setIsAutoDetected] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  
  // Get calculator data for saving
  const calculator = useUnifiedCalculator();
  const autoOptions = useState<AutoCalculateOptions>({ propertyTax: true, insurance: true, pmi: true, closingCosts: true });
  const { mortgageData } = calculator;
  const draft = useCalculatorDraft({ mortgageData, setMortgageData: calculator.setMortgageData, step: 'main' });

  const hasCalculation = mortgageData.monthlyPayment.total > 0;

  // Auto-detect screen size on mount
  useEffect(() => {
    const detectScreenSize = () => {
      const isMobile = window.innerWidth < 768;
      if (isAutoDetected) {
        setViewMode(isMobile ? 'mobile' : 'desktop');
      }
    };

    detectScreenSize();
    window.addEventListener('resize', detectScreenSize);
    return () => window.removeEventListener('resize', detectScreenSize);
  }, [isAutoDetected]);

  const handleModeSwitch = (mode: ViewMode) => {
    setIsAutoDetected(false);
    setViewMode(mode);
  };

  // Prepare calculation data for the save modal
  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  const calculationData = {
    monthlyPayment: Math.round(mortgageData.monthlyPayment.total),
    homePrice: mortgageData.homePrice,
    downPayment: mortgageData.downPayment,
    loanAmount,
    interestRate: mortgageData.interestRate,
    closingCosts: mortgageData.closingCosts?.total || 0,
    mortgageData
  };

  return (
    <PageTransition>
    <div className="duo-page min-h-dvh bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950 flex flex-col">
      {/* Compact Header */}
      <header className="calculator-header sticky top-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-border/40">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 max-w-7xl mx-auto">
          {/* Logo */}
          <Link to="/home" className="flex items-center gap-2">
            <img
              src={images.equiforgeLogoFlat}
              alt="EquiForge"
              className="h-8 md:h-10 w-auto object-contain"
            />
          </Link>

          {/* View Toggle & Actions */}
          <div className="calculator-toolbar flex flex-wrap items-center gap-2">
            {/* Calculator Navigation */}
            <CalculatorNav />

            <div className="flex bg-slate-800/50 rounded-lg p-1 border border-border/40">
              <button aria-label="Automatically adapt layout" aria-pressed={isAutoDetected} onClick={() => setIsAutoDetected(true)} className={cn("min-h-11 px-3 rounded-md text-sm", isAutoDetected ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>Auto</button>
              <button
                aria-label="Use desktop layout"
                aria-pressed={viewMode === 'desktop'}
                onClick={() => handleModeSwitch('desktop')}
                className={cn(
                  "flex min-h-11 min-w-11 items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
                  viewMode === 'desktop'
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-slate-700/50"
                )}
              >
                <Monitor className="w-4 h-4" />
                <span className="hidden lg:inline">Desktop</span>
              </button>
              <button
                aria-label="Use mobile layout"
                aria-pressed={viewMode === 'mobile'}
                onClick={() => handleModeSwitch('mobile')}
                className={cn(
                  "flex min-h-11 min-w-11 items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
                  viewMode === 'mobile'
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-slate-700/50"
                )}
              >
                <Smartphone className="w-4 h-4" />
                <span className="hidden lg:inline">Mobile</span>
              </button>
            </div>

            {/* Save Calculation Button */}
            {hasCalculation && (
              <Button 
                onClick={() => {
                  if (isAuthenticated) {
                    setShowSaveModal(true);
                  } else {
                    // Redirect to login with return path
                    navigate('/login', { state: { returnTo: '/try/payment' } });
                  }
                }} 
                variant="outline" 
                size="sm" 
                className="border-border/60 bg-slate-800/50 hover:bg-slate-700/50"
              >
                <Save className="w-4 h-4 mr-1.5" />
                <span className="hidden lg:inline">{isAuthenticated ? 'Save' : 'Sign in to Save'}</span>
                <span className="lg:hidden">Save</span>
              </Button>
            )}

            {/* Sign In Button - only show if not authenticated */}
            {!isAuthenticated && (
              <Button asChild variant="outline" size="sm" className="hidden lg:flex border-border/60">
                <Link to="/login">Sign In</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Calculator Content */}
      <main className="flex-1 relative">
        <AnimatePresence mode="wait">
          {viewMode === 'desktop' ? (
            <motion.div
              key="desktop"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              <div className="max-w-7xl mx-auto px-4 py-6">
                <IntegratedCalculatorContent calculator={calculator} draft={draft} autoOptions={autoOptions} />
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="mobile"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              <MobileCalculatorContent calculator={calculator} autoOptions={autoOptions} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Floating CTA Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="calculator-promo relative z-40 bg-gradient-to-r from-purple-900/90 via-slate-900/95 to-cyan-900/90 backdrop-blur-xl border-t border-border/40"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-slate-300">
              <span className="hidden sm:inline">Want SmartBuy comparison, AI insights & more? </span>
              <span className="sm:hidden">Unlock all tools </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild size="sm" className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700">
              <Link to="/signup">
                Sign Up Free
                <ArrowRight className="ml-1.5 w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="ghost" size="sm" className="text-slate-400 hover:text-slate-200 sm:flex hidden">
              <Link to="/home">Learn More</Link>
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Save Calculation Modal - only for authenticated users */}
      {isAuthenticated && (
        <SaveCalculationModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          calculationData={calculationData}
          userEmail={user?.email}
        />
      )}
    </div>
    </PageTransition>
  );
};

export default TryCalculator;
